import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  addCategory, 
  updateCategory, 
  deleteCategory,
  pageCategories,
  getCategoryDetail,
  getCategoryTree,
  listAllCategories,
  batchAddSubCategories
} from '../../api/jadeIdentificationCategory';
import { message } from 'antd';

export const fetchCategoryList = createAsyncThunk(
  'jadeIdentificationCategory/fetchList',
  async ({ page, pageSize, ...queryParams }, { rejectWithValue }) => {
    try {
      const response = await pageCategories(page, pageSize, queryParams);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取分类列表失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchCategoryDetail = createAsyncThunk(
  'jadeIdentificationCategory/fetchDetail',
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await getCategoryDetail(categoryId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取分类详情失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchCategoryTree = createAsyncThunk(
  'jadeIdentificationCategory/fetchTree',
  async ({ status, name }, { rejectWithValue }) => {
    try {
      const response = await getCategoryTree(status, name);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取分类树失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAllCategories = createAsyncThunk(
  'jadeIdentificationCategory/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await listAllCategories();
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取所有分类失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const addCategoryAsync = createAsyncThunk(
  'jadeIdentificationCategory/add',
  async (category, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await addCategory(category);
      message.success('添加成功');
      const state = getState().jadeIdentificationCategory;
      dispatch(fetchCategoryList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      dispatch(fetchCategoryTree({}));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '添加失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCategoryAsync = createAsyncThunk(
  'jadeIdentificationCategory/update',
  async (category, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateCategory(category);
      message.success('更新成功');
      const state = getState().jadeIdentificationCategory;
      dispatch(fetchCategoryList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      dispatch(fetchCategoryTree({}));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteCategoryAsync = createAsyncThunk(
  'jadeIdentificationCategory/delete',
  async (categoryId, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await deleteCategory(categoryId);
      message.success('删除成功');
      const state = getState().jadeIdentificationCategory;
      dispatch(fetchCategoryList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      dispatch(fetchCategoryTree({}));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const batchAddSubCategoriesAsync = createAsyncThunk(
  'jadeIdentificationCategory/batchAddSub',
  async ({ parentId, categoryNames }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await batchAddSubCategories(parentId, categoryNames);
      message.success('批量添加成功');
      const state = getState().jadeIdentificationCategory;
      dispatch(fetchCategoryList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      dispatch(fetchCategoryTree({}));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '批量添加失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const jadeIdentificationCategorySlice = createSlice({
  name: 'jadeIdentificationCategory',
  initialState: {
    list: [],
    total: 0,
    page: 1,
    pageSize: 10,
    loading: false,
    currentCategory: null,
    categoryTree: [],
    queryParams: {}
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setQueryParams: (state, action) => {
      state.queryParams = action.payload;
    },
    setCurrentCategory: (state, action) => {
      state.currentCategory = action.payload;
    },
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCategoryList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCategoryList.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.page = action.payload.current || action.payload.page || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchCategoryList.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(fetchCategoryDetail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCategoryDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.currentCategory = action.payload;
    });
    builder.addCase(fetchCategoryDetail.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(fetchCategoryTree.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCategoryTree.fulfilled, (state, action) => {
      state.loading = false;
      state.categoryTree = action.payload;
    });
    builder.addCase(fetchCategoryTree.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(addCategoryAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addCategoryAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addCategoryAsync.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateCategoryAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCategoryAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateCategoryAsync.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteCategoryAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteCategoryAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteCategoryAsync.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const { setPage, setPageSize, setQueryParams, setCurrentCategory, clearCurrentCategory } = jadeIdentificationCategorySlice.actions;
export default jadeIdentificationCategorySlice.reducer;
